import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useCompanies, useArchiveCompany, useUnarchiveCompany } from '@/lib/adminApi';
import type { CompanySummary } from '@ventin/shared';
import { CompanyFormDialog } from './CompanyFormDialog';
import { ArchiveCompanyDialog } from './ArchiveCompanyDialog';

/**
 * CompaniesPage — /admin/companies (COMP-01 / COMP-02 / COMP-03).
 *
 * Wiring-only: every hook, schema, and API route already exists. This page
 * composes them into a usable admin surface.
 *
 * Rendering: shadcn primitives only (no shadcn Table — we inline a plain
 * table element to avoid adding a new registry component mid-phase).
 */
export default function CompaniesPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, isLoading, isError } = useCompanies({ includeArchived });

  // Dialog state lives in the page — children are pure controlled components.
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<CompanySummary | undefined>(undefined);
  const [archiveTarget, setArchiveTarget] = useState<CompanySummary | null>(null);

  const archiveMut = useArchiveCompany();
  const unarchiveMut = useUnarchiveCompany();

  const companies = useMemo(() => data?.companies ?? [], [data]);

  async function handleConfirmArchive() {
    if (!archiveTarget) return;
    try {
      if (archiveTarget.isArchived) {
        await unarchiveMut.mutateAsync({ id: archiveTarget.id });
        toast.success('Company unarchived');
      } else {
        await archiveMut.mutateAsync({ id: archiveTarget.id });
        toast.success('Company archived');
      }
      setArchiveTarget(null);
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold leading-[1.3]">Companies</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create, edit, and archive tenant companies.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
          <Button
            onClick={() => {
              setEditing(undefined);
              setFormMode('create');
            }}
          >
            New Company
          </Button>
        </div>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading companies…</p>}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load companies — try again.</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">GA Override</th>
                <th className="px-4 py-3 font-medium">Updated</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No companies yet.
                  </td>
                </tr>
              )}
              {companies.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/admin/companies/${c.id}`}
                      className="hover:text-primary hover:underline"
                      title={`Open ${c.name}`}
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.slug}</td>
                  <td className="px-4 py-3">
                    <StatusBadge archived={c.isArchived} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.gaPropertyIdOverride ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(c.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditing(c);
                          setFormMode('edit');
                        }}
                        aria-label={`Edit ${c.name}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setArchiveTarget(c)}
                        aria-label={`${c.isArchived ? 'Unarchive' : 'Archive'} ${c.name}`}
                      >
                        {c.isArchived ? 'Unarchive' : 'Archive'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit dialog — single component instance, mode toggles behavior. */}
      <CompanyFormDialog
        mode={formMode ?? 'create'}
        initial={editing}
        open={formMode !== null}
        onOpenChange={(o) => {
          if (!o) setFormMode(null);
        }}
      />

      {/* Archive/unarchive confirm dialog */}
      <ArchiveCompanyDialog
        open={archiveTarget !== null}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        mode={archiveTarget?.isArchived ? 'unarchive' : 'archive'}
        companyName={archiveTarget?.name ?? ''}
        busy={archiveMut.isPending || unarchiveMut.isPending}
        onConfirm={handleConfirmArchive}
      />
    </div>
  );
}

function StatusBadge({ archived }: { archived: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        archived ? 'bg-[#F1F1F3] text-[#4B4B52]' : 'bg-[#ECFDF5] text-[#16A34A]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-[6px] w-[6px] rounded-full',
          archived ? 'bg-[#8A8A94]' : 'bg-[#16A34A]',
        )}
      />
      {archived ? 'Archived' : 'Active'}
    </span>
  );
}
